import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { API_URL } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ShoppingCart from '../components/marketplace/ShoppingCart';
import CheckoutForm, { CheckoutFormData } from '../components/marketplace/CheckoutForm';
import OrderConfirmation from '../components/marketplace/OrderConfirmation';
import CheckoutResult from '../components/marketplace/CheckoutResult';
import { dashboardLoginUrl } from '../config/esanzaContent';
import { MarketplaceCartItem, ConfirmedOrderItem } from '../components/marketplace/types';
import {
  CART_STORAGE_KEY,
  DEFAULT_CURRENCY,
  clampQuantity,
  computeSubtotal,
  constraintsFromItem,
  constraintsFromListing,
  isOrderable,
  restoreCart,
  serializeCart,
  validateQuantity,
} from '../components/marketplace/cartUtils';

interface Listing {
  id: number;
  title: string;
  description: string;
  grade: string;
  certification: string;
  commercial_unit: string;
  normalized_unit: string;
  price_per_unit: number;
  currency: string;
  minimum_order_quantity: number;
  maximum_order_quantity: number;
  available_quantity: number;
  image_url: string;
  traceability_enabled: boolean;
  fulfillment_location: string;
  fulfillment_city: string;
  delivery_areas: string | null;
  harvest_start_date: string;
  harvest_end_date: string;
  source_harvest_lots: any;
  source_warehouse_lots: any;
  created_at: string;
  published_at: string;
  catalog_name: string;
  catalog_category: string;
  crop_type: string;
  catalog_description: string;
  supplier_profile_id: number;
  supplier_description: string;
  supplier_email: string;
  supplier_phone: string;
  supplier_address: string;
  supplier_type: string;
  related?: Listing[];
  base_unit: string;
  conversion_factor: number;
  tenant_id: number;
  supplier_name: string;
  supplier_verified: string;
  supplier_logo: string;
  supplier_city: string;
  supplier_country: string;
  tenant_name: string;
  tenant_slug: string;
}

interface Category {
  category: string;
  listing_count: number;
}

type ViewMode = 'catalog' | 'checkout' | 'confirmation';

/** Resolve a weekday name ("Wednesday") to the next occurrence as an ISO date. */
const WEEKDAY_INDEX: Record<string, number> = {
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7,
};
function nextWeekdayDate(dayName: string): string {
  const target = WEEKDAY_INDEX[String(dayName || '').trim().toLowerCase()];
  if (!target) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDow = today.getDay() === 0 ? 7 : today.getDay();
  const daysAhead = (target - todayDow + 7) % 7;
  const next = new Date(today);
  next.setDate(today.getDate() + daysAhead);
  return next.toISOString().slice(0, 10);
}

const Marketplace: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [listings, setListings] = useState<Listing[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [faqOpen, setFaqOpen] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Advanced filter state
  const [grade, setGrade] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [traceabilityOnly, setTraceabilityOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);

  // Derived filter data
  const [suppliers, setSuppliers] = useState<Array<{id: number; business_name: string; city: string; active_listing_count: number}>>([]);
  const [availableGrades, setAvailableGrades] = useState<string[]>([]);
  const [displayListings, setDisplayListings] = useState<Listing[]>([]);

  // One currency for the whole price filter and share sheet: whatever the
  // catalogue is priced in, falling back to the platform default when the
  // first page has not loaded yet.
  const marketCurrency = listings.find(l => l.currency)?.currency || DEFAULT_CURRENCY;

  const [cartItems, setCartItems] = useState<MarketplaceCartItem[]>(() => {
    const LEGACY_V2_KEY = 'esanza_marketplace_cart_v2';
    try {
      // v3 is the current versioned payload. If absent, migrate the legacy v2
      // cart: sanitize it once, save as v3, and remove v2.
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const items = restoreCart(JSON.parse(saved));
        if (items.length > 0) return items;
      }
      const legacy = localStorage.getItem(LEGACY_V2_KEY);
      if (legacy) {
        const items = restoreCart(JSON.parse(legacy));
        localStorage.removeItem(LEGACY_V2_KEY);
        if (items.length > 0) return items;
      }
      return [];
    } catch { return []; }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [detailListing, setDetailListing] = useState<Listing | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'overview' | 'traceability' | 'delivery'>('overview');
  const [viewMode, setViewMode] = useState<ViewMode>('catalog');
  const [confirmedOrder, setConfirmedOrder] = useState<any>(null);
  const [checkoutPhase, setCheckoutPhase] = useState<'not_submitted' | 'submitting' | 'order_created' | 'confirmation_loading' | 'confirmed' | 'created_but_confirmation_unavailable' | 'submission_failed'>('not_submitted');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [createdOrderNumber, setCreatedOrderNumber] = useState<string | null>(null);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);

  // Fetch full detail when a listing is selected
  useEffect(() => {
    if (!selectedListing) { setDetailListing(null); setDetailTab('overview'); return; }
    let cancelled = false;
    setDetailLoading(true);
    fetch(`${API_URL}/api/marketplace/public/listings/${selectedListing.id}`)
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.success && data.item) setDetailListing(data.item);
      })
      .catch(() => {}) // silently fail — modal still works with list data
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedListing?.id]);

  const d = detailListing || selectedListing; // convenience: prefer detail, fallback to list data

  // Persist cart (versioned payload; corrupt legacy carts are migrated on load)
  useEffect(() => {
    try { localStorage.setItem(CART_STORAGE_KEY, serializeCart(cartItems)); } catch {}
  }, [cartItems]);

  // Fetch categories, suppliers, and build grade list on mount
  useEffect(() => {
    fetch(`${API_URL}/api/marketplace/public/categories`)
      .then(r => r.json())
      .then(data => { if (data.success) setCategories(data.categories || []); })
      .catch(() => {});

    fetch(`${API_URL}/api/marketplace/public/suppliers`)
      .then(r => r.json())
      .then(data => { if (data.success) setSuppliers(data.suppliers || []); })
      .catch(() => {});
  }, []);

  // Fetch listings with all filter params
  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('category', selectedCategory);
      if (grade) params.set('grade', grade);
      if (minPrice) params.set('min_price', minPrice);
      if (maxPrice) params.set('max_price', maxPrice);
      if (supplierId) params.set('supplier_id', supplierId);
      params.set('sort', sortBy);
      params.set('order', sortOrder);
      params.set('page', String(page));
      params.set('limit', '24');

      const res = await fetch(`${API_URL}/api/marketplace/public/listings?${params}`);
      if (!res.ok) throw new Error('Failed to load marketplace');
      const data = await res.json();
      const items = data.items || [];
      setListings(items);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);

      // Extract unique grades from all results for the filter dropdown
      const gradeSet = new Set<string>();
      items.forEach((item: Listing) => { if (item.grade) gradeSet.add(item.grade); });
      if (gradeSet.size > 0) setAvailableGrades(Array.from(gradeSet).sort());
    } catch (err: any) {
      setError(err.message || 'Marketplace is unavailable. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory, grade, minPrice, maxPrice, supplierId, sortBy, sortOrder, page]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  // Client-side post-filtering for traceability and availability
  useEffect(() => {
    let filtered = [...listings];
    if (traceabilityOnly) filtered = filtered.filter(l => l.traceability_enabled);
    if (inStockOnly) filtered = filtered.filter(l => (l.available_quantity || 0) > 0);
    setDisplayListings(filtered);
  }, [listings, traceabilityOnly, inStockOnly]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, selectedCategory, sortBy, grade, minPrice, maxPrice, supplierId]);

  const makeCartItem = (listing: Listing, quantity: number): MarketplaceCartItem => {
    const pricePerUnit = Number(listing.price_per_unit) || 0;
    const constraints = constraintsFromListing(listing);
    const qty = clampQuantity(quantity, constraints);
    return {
      listingId: listing.id,
      title: listing.title,
      catalogName: listing.catalog_name,
      cropType: listing.catalog_name || listing.title,
      quantity: qty,
      pricePerUnit,
      unit: listing.commercial_unit,
      normalizedUnit: listing.normalized_unit || listing.commercial_unit,
      conversionFactor: Number(listing.conversion_factor) || 1,
      currency: listing.currency || DEFAULT_CURRENCY,
      subtotal: computeSubtotal(qty, pricePerUnit),
      supplierName: listing.supplier_name,
      tenantId: Number(listing.tenant_id) || 0,
      minimumOrderQuantity: constraints.minimumOrderQuantity,
      maximumOrderQuantity: constraints.maximumOrderQuantity,
      availableQuantity: constraints.availableQuantity,
      quantityStep: constraints.quantityStep,
      imageUrl: listing.image_url || undefined,
    };
  };

  const handleAddToCart = (listing: Listing, requestedQuantity: number) => {
    const constraints = constraintsFromListing(listing);
    // If available stock cannot meet the minimum order, do NOT add the item.
    // Clamping it into the minimum would create an invalid order.
    if (!isOrderable(constraints)) return;
    const addQty = clampQuantity(requestedQuantity, constraints);
    if (addQty <= 0) return;
    setCartItems(prev => {
      const existing = prev.find(item => item.listingId === listing.id);
      if (existing) {
        return prev.map(item =>
          item.listingId === listing.id
            ? (() => {
                // Numeric addition on the restored number — never string concat.
                const newQty = clampQuantity(item.quantity + addQty, constraintsFromItem(item));
                return { ...item, quantity: newQty, subtotal: computeSubtotal(newQty, item.pricePerUnit) };
              })()
            : item
        );
      }
      return [...prev, makeCartItem(listing, addQty)];
    });
    setIsCartOpen(true);
  };

  const handleUpdateQuantity = (listingId: number, quantity: number) => {
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setCartItems(prev => prev.filter(item => item.listingId !== listingId));
      return;
    }
    setCartItems(prev =>
      prev.map(item => {
        if (item.listingId !== listingId) return item;
        const constraints = constraintsFromItem(item);
        const qty = clampQuantity(quantity, constraints);
        return { ...item, quantity: qty, subtotal: computeSubtotal(qty, item.pricePerUnit) };
      })
    );
  };

  const handleRemoveItem = (listingId: number) => {
    setCartItems(prev => prev.filter(item => item.listingId !== listingId));
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/buy' } } });
      return;
    }
    setIsCartOpen(false);
    setViewMode('checkout');
  };

  const mapServerItemsToConfirmed = (rawItems: any[]): ConfirmedOrderItem[] =>
    rawItems.map(item => ({
      listingId: Number(item.listing_id ?? item.listingId ?? 0),
      title: item.title || item.catalog_name || 'Product',
      grade: item.grade || null,
      quantity: Number(item.commercial_quantity ?? item.quantity ?? 0),
      unit: item.commercial_unit || item.unit || 'unit',
      unitPrice: Number(item.unit_price ?? item.unitPrice ?? 0),
      currency: item.currency || DEFAULT_CURRENCY,
      subtotal: Number(item.total_price ?? item.totalPrice ?? 0),
    }));

  const handleSubmitOrder = async (formData: CheckoutFormData) => {
    try {
      setCheckoutError(null);

      // Validate every line against its listing constraints before submitting.
      for (const item of cartItems) {
        const errors = validateQuantity(item.quantity, constraintsFromItem(item));
        if (errors.length > 0) {
          throw new Error(`${item.catalogName || item.title}: ${errors.join(' ')}`);
        }
      }

      const cartFingerprint = cartItems.map(item => `${item.listingId}:${item.quantity}`).sort().join('|');
      const storageKey = `esanza_checkout_idempotency:${cartFingerprint}`;
      let idempotencyKey = sessionStorage.getItem(storageKey);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        sessionStorage.setItem(storageKey, idempotencyKey);
      }
      const payload = {
        items: cartItems.map(item => ({
          listing_id: item.listingId,
          quantity: item.quantity,
        })),
        delivery_address: formData.deliveryAddress,
        delivery_date: nextWeekdayDate(formData.deliveryDay),
        payment_terms: 'manual_payment',
        notes: `Contact: ${formData.buyerName}, ${formData.phone}. ${formData.specialInstructions || ''}`,
        idempotency_key: idempotencyKey,
      };

      const token = localStorage.getItem('esanza_buyer_token');
      const res = await fetch(`${API_URL}/api/marketplace/buyer/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || errData.message || 'Failed to submit order');
      }

      const orderData = await res.json();

      // Build the confirmation EXCLUSIVELY from server-confirmed values.
      // If the POST response lacks line items, fetch the created order's
      // detail. Never present client cart totals as confirmed.
      const orderId = Number(orderData.order?.id);
      const orderNumber = orderData.order?.order_number || 'BPO-' + Date.now();

      // Phase 1: Order creation succeeded (HTTP 200/201 with a persisted order ID).
      // We now own the order; enrich confirmation from server-confirmed data.
      setCheckoutPhase('order_created');
      setCreatedOrderNumber(orderNumber);
      setCreatedOrderId(orderId);

      let serverItems = Array.isArray(orderData.items) ? orderData.items : null;
      let serverOrder = orderData.order || null;

      // Phase 2: Try to enrich from the POST response first.
      // If items are missing, fetch the order detail as a fallback.
      if (!serverItems && orderId > 0) {
        setCheckoutPhase('confirmation_loading');
        try {
          const detailRes = await fetch(`${API_URL}/api/marketplace/buyer/orders/${orderId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (detailRes.ok) {
            const detail = await detailRes.json();
            serverOrder = detail.order || serverOrder;
            serverItems = Array.isArray(detail.items) ? detail.items : null;
          }
        } catch (detailErr) {
          console.warn('Order detail enrichment failed:', detailErr);
        }
      }

      if (Array.isArray(serverItems) && serverItems.length > 0) {
        // Full confirmation available — show the standard confirmation screen.
        const confirmedItems = mapServerItemsToConfirmed(serverItems);
        const confirmedTotal = Number(serverOrder?.total_amount ?? confirmedItems.reduce((sum, i) => sum + i.subtotal, 0));
        const confirmedCurrency = serverOrder?.currency || orderData.currency || cartItems[0]?.currency || DEFAULT_CURRENCY;

        setConfirmedOrder({
          orderNumber,
          items: confirmedItems,
          totalValue: confirmedTotal,
          currency: confirmedCurrency,
          deliveryDay: formData.deliveryDay,
          deliveryDate: orderData.order?.delivery_date || '',
          buyerName: formData.buyerName,
        });
        setCheckoutPhase('confirmed');
        setViewMode('confirmation');
        // Clear only the purchased cart lines from React state and versioned
        // local storage after the order has been reconciled locally.
        setCartItems([]);
        sessionStorage.removeItem(storageKey);
        fetchListings();
      } else {
        // Order was created (persisted order ID) but confirmation detail is
        // unavailable. Do NOT throw — the order exists. Show recovery screen.
        setCheckoutPhase('created_but_confirmation_unavailable');
        setViewMode('confirmation');
        setConfirmedOrder({
          orderNumber,
          items: [],
          totalValue: 0,
          currency: orderData.currency || cartItems[0]?.currency || DEFAULT_CURRENCY,
          deliveryDay: formData.deliveryDay,
          deliveryDate: '',
          buyerName: formData.buyerName,
          orderId,
        });
        // Keep the cart intact so the buyer can retry viewing the order,
        // but clear the idempotency key only after we've recorded the order ID.
        sessionStorage.removeItem(storageKey);
      }
    } catch (error: any) {
      console.error('Order error:', error);
      // True failed/aborted create request — preserve the cart and keep the
      // ownership-scoped idempotency key so a retry replays the same intent.
      setCheckoutPhase('submission_failed');
      setCheckoutError(error.message || 'Order submission failed. Please try again.');
    }
  };

  const totalCartValue = cartItems.reduce((sum, item) => sum + item.subtotal, 0);

  const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Marketplace | eSanza"
        description="Browse verified agricultural products from eSanza certified suppliers."
      />
      <Header />

      {viewMode === 'catalog' && (
        <>
          {/* Hero Banner */}
          <div className="relative bg-[#06150e] overflow-hidden">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #ffffff 1px, transparent 0)', backgroundSize: '36px 36px' }} />
            <div className="absolute -top-32 right-[-8%] h-[420px] w-[420px] rounded-full bg-[#d4af37]/20 blur-[120px]" />
            <div className="absolute bottom-[-40%] left-[-10%] h-[380px] w-[380px] rounded-full bg-[#14532d]/50 blur-[120px]" />
            <div className="relative max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 pt-28 pb-14 md:pt-32 md:pb-16">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
                <Link to="/" className="transition hover:text-[#f3d978]">Home</Link>
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                <span className="text-[#f3d978]">Marketplace</span>
              </div>

              <div className="mt-6 max-w-3xl">
                <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[0.98] text-white">
                  Farm-fresh, <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f3d978] to-[#d4af37]">direct from the source</span>
                </h1>
                <p className="mt-5 max-w-xl text-lg font-medium leading-relaxed">
                  <span className="text-[#f3d978]">Verified farms, cooperatives and agricultural suppliers.</span>{' '}
                  <span className="text-sky-200">Transparent prices, full traceability, and produce picked at the right time.</span>
                </p>

                {/* Trust chips */}
                <div className="mt-6 flex flex-wrap gap-2.5">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-xs font-bold text-white/85 backdrop-blur">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {total > 0 ? `${total} products available` : 'Live inventory'}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-xs font-bold text-white/85 backdrop-blur">
                    <svg className="h-3.5 w-3.5 text-[#f3d978]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    Verified suppliers
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-xs font-bold text-white/85 backdrop-blur">
                    <svg className="h-3.5 w-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Fully traceable
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-xs font-bold text-white/85 backdrop-blur">
                    <svg className="h-3.5 w-3.5 text-[#d4af37]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Direct pricing
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filters Bar */}
          <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
            <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="flex-1 relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search products, suppliers, categories..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:bg-white focus:ring-0 outline-none transition-all"
                  />
                </div>

                {/* Sort */}
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [s, o] = e.target.value.split('-');
                    setSortBy(s);
                    setSortOrder(o);
                  }}
                  className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:border-emerald-500 outline-none"
                >
                  <option value="created_at-DESC">Newest First</option>
                  <option value="created_at-ASC">Oldest First</option>
                  <option value="price_per_unit-ASC">Price: Low to High</option>
                  <option value="price_per_unit-DESC">Price: High to Low</option>
                  <option value="available_quantity-DESC">Most Available</option>
                  <option value="title-ASC">Name A-Z</option>
                </select>

                {/* Filter toggle */}
                <button
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`relative px-4 py-3 rounded-xl font-bold text-sm border transition-all flex items-center gap-2 ${
                    filterOpen || grade || minPrice || maxPrice || supplierId || traceabilityOnly || inStockOnly
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                  Filters
                  {(grade || minPrice || maxPrice || supplierId || traceabilityOnly || inStockOnly) && (
                    <span className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                      {[grade, minPrice || maxPrice, supplierId, traceabilityOnly, inStockOnly].filter(Boolean).length}
                    </span>
                  )}
                </button>

                {/* Cart */}
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative px-5 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-lg transition-all flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Cart
                  {cartItems.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center border-2 border-white font-bold">
                      {cartItems.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Category pills */}
              {categories.length > 0 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                      !selectedCategory
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({total})
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.category}
                      onClick={() => setSelectedCategory(selectedCategory === cat.category ? '' : cat.category)}
                      className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                        selectedCategory === cat.category
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {capitalize(cat.category)} ({cat.listing_count})
                    </button>
                  ))}
                </div>
              )}

              {/* Active filter chips */}
              {(grade || minPrice || maxPrice || supplierId || traceabilityOnly || inStockOnly) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {grade && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                      Grade: {grade}
                      <button onClick={() => setGrade('')} className="hover:text-emerald-900">×</button>
                    </span>
                  )}
                  {minPrice && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                      Min: {minPrice} {marketCurrency}
                      <button onClick={() => setMinPrice('')} className="hover:text-emerald-900">×</button>
                    </span>
                  )}
                  {maxPrice && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                      Max: {maxPrice} {marketCurrency}
                      <button onClick={() => setMaxPrice('')} className="hover:text-emerald-900">×</button>
                    </span>
                  )}
                  {supplierId && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                      Supplier: {suppliers.find(s => String(s.id) === supplierId)?.business_name || supplierId}
                      <button onClick={() => setSupplierId('')} className="hover:text-emerald-900">×</button>
                    </span>
                  )}
                  {traceabilityOnly && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                      Traceable only
                      <button onClick={() => setTraceabilityOnly(false)} className="hover:text-emerald-900">×</button>
                    </span>
                  )}
                  {inStockOnly && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-700">
                      In stock only
                      <button onClick={() => setInStockOnly(false)} className="hover:text-emerald-900">×</button>
                    </span>
                  )}
                  <button
                    onClick={() => { setGrade(''); setMinPrice(''); setMaxPrice(''); setSupplierId(''); setTraceabilityOnly(false); setInStockOnly(false); }}
                    className="text-xs font-bold text-gray-500 hover:text-gray-700 px-2 py-1"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Advanced Filter Panel */}
            {filterOpen && (
              <div className="border-t border-gray-100 bg-gray-50">
                <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Grade */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Grade</label>
                      <select
                        value={grade}
                        onChange={(e) => setGrade(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald-500 outline-none"
                      >
                        <option value="">All grades</option>
                        {availableGrades.map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </div>

                    {/* Supplier */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Supplier</label>
                      <select
                        value={supplierId}
                        onChange={(e) => setSupplierId(e.target.value)}
                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald-500 outline-none"
                      >
                        <option value="">All suppliers</option>
                        {suppliers.map(s => (
                          <option key={s.id} value={s.id}>{s.business_name} ({s.active_listing_count})</option>
                        ))}
                      </select>
                    </div>

                    {/* Price Range */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Price Range ({marketCurrency})</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          placeholder="Min"
                          min="0"
                          className="w-1/2 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald-500 outline-none"
                        />
                        <input
                          type="number"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          placeholder="Max"
                          min="0"
                          className="w-1/2 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:border-emerald-500 outline-none"
                        />
                      </div>
                    </div>

                    {/* Availability & Traceability toggles */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Availability</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inStockOnly}
                            onChange={(e) => setInStockOnly(e.target.checked)}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-700">In stock only</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={traceabilityOnly}
                            onChange={(e) => setTraceabilityOnly(e.target.checked)}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-700">Traceable only</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Reset button */}
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => { setGrade(''); setMinPrice(''); setMaxPrice(''); setSupplierId(''); setTraceabilityOnly(false); setInStockOnly(false); setFilterOpen(false); }}
                      className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition"
                    >
                      Reset all filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Product Grid */}
          <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="text-red-700 font-medium">{error}</p>
                <button onClick={fetchListings} className="mt-2 text-sm text-red-600 font-bold hover:underline">Try again</button>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                    <div className="aspect-[4/3] bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                      <div className="h-6 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-green-100 rounded-3xl flex items-center justify-center mx-auto">
                  <span className="text-5xl">🌱</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-6">
                  {search || selectedCategory || grade || minPrice || maxPrice || supplierId || traceabilityOnly || inStockOnly ? 'No products match your filters' : 'No products available yet'}
                </h3>
                <p className="mt-2 text-gray-500 max-w-md mx-auto">
                  {search || selectedCategory || grade || minPrice || maxPrice || supplierId || traceabilityOnly || inStockOnly
                    ? 'Try adjusting your search or browse different categories.'
                    : 'Fresh products from verified suppliers will appear here. Check back soon!'}
                </p>
                {(search || selectedCategory || grade || minPrice || maxPrice || supplierId || traceabilityOnly || inStockOnly) && (
                  <button
                    onClick={() => { setSearch(''); setSelectedCategory(''); setGrade(''); setMinPrice(''); setMaxPrice(''); setSupplierId(''); setTraceabilityOnly(false); setInStockOnly(false); }}
                    className="mt-4 px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between pb-5">
                  <p className="text-sm text-gray-500">
                    <span className="font-bold text-gray-900">{displayListings.length}</span> {displayListings.length === 1 ? 'product' : 'products'}
                    {displayListings.length !== total && (
                      <span className="text-gray-400"> of {total} total</span>
                    )}
                    {(() => {
                      const section = displayListings[0]?.catalog_category;
                      return section ? <span className="text-gray-400"> · {capitalize(section)}</span> : null;
                    })()}
                  </p>
                  {search && (
                    <p className="text-sm text-gray-500">
                      Showing results for "<span className="font-semibold text-gray-700">{search}</span>"
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {displayListings.map(listing => (
                    <div
                      key={listing.id}
                      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                      onClick={() => setSelectedListing(listing)}
                    >
                      {/* Image */}
                      <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                        {listing.image_url ? (
                          <img
                            src={listing.image_url}
                            alt={listing.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-4xl">🌿</span>
                          </div>
                        )}
                        {listing.traceability_enabled && (
                          <span className="absolute top-3 left-3 px-2 py-1 bg-emerald-600 text-white text-[10px] font-bold rounded-md flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Traceable
                          </span>
                        )}
                        {listing.supplier_verified === 'verified' && (
                          <span className="absolute top-3 right-3 px-2 py-1 bg-blue-600 text-white text-[10px] font-bold rounded-md flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                            Verified
                          </span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <h3 className="font-bold text-gray-900 text-sm leading-tight truncate">{listing.title}</h3>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{listing.catalog_name || listing.catalog_category}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 mb-2">
                          {listing.supplier_logo ? (
                            <img src={listing.supplier_logo} alt="" className="w-5 h-5 rounded bg-emerald-100 object-cover" />
                          ) : (
                            <div className="w-5 h-5 rounded bg-emerald-100 flex items-center justify-center">
                              <span className="text-[10px] font-bold text-emerald-700">{listing.supplier_name?.[0] || 'S'}</span>
                            </div>
                          )}
                          <span className="text-xs text-gray-600 truncate">{listing.supplier_name}</span>
                          {listing.supplier_verified === 'verified' && (
                            <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                          )}
                        </div>

                        {/* Price */}
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-lg font-extrabold text-emerald-600">{listing.currency || DEFAULT_CURRENCY} {listing.price_per_unit?.toLocaleString()}</span>
                          <span className="text-xs text-gray-400">/ {listing.commercial_unit}</span>
                        </div>

                        {/* Stock status */}
                        <div className="mt-2 flex items-center gap-2">
                          {(listing.available_quantity || 0) > 0 ? (
                            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                              In Stock
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 font-medium">Out of Stock</span>
                          )}
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-400">Min {listing.minimum_order_quantity || 1} {listing.commercial_unit}</span>
                        </div>

                        {/* Tags */}
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {listing.grade && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded">{listing.grade}</span>
                          )}
                          {listing.catalog_category && (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-medium rounded">{capitalize(listing.catalog_category)}</span>
                          )}
                          {listing.supplier_city && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-medium rounded">{listing.supplier_city}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <span className="text-sm text-gray-500 px-4">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                )}

                {/* Minimal FAQ strip */}
                <div className="mt-12 border-t border-gray-100 pt-8">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                    <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gray-400 shrink-0">Quick questions</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <button type="button" onClick={() => setFaqOpen(faqOpen === 'delivery' ? '' : 'delivery')} className="text-sm text-gray-600 hover:text-[#14532d] transition font-medium">
                        Delivery {faqOpen === 'delivery' ? '↑' : '↓'}
                      </button>
                      <button type="button" onClick={() => setFaqOpen(faqOpen === 'payment' ? '' : 'payment')} className="text-sm text-gray-600 hover:text-[#14532d] transition font-medium">
                        Payment {faqOpen === 'payment' ? '↑' : '↓'}
                      </button>
                      <button type="button" onClick={() => setFaqOpen(faqOpen === 'traceability' ? '' : 'traceability')} className="text-sm text-gray-600 hover:text-[#14532d] transition font-medium">
                        Traceability {faqOpen === 'traceability' ? '↑' : '↓'}
                      </button>
                      <button type="button" onClick={() => setFaqOpen(faqOpen === 'pricing' ? '' : 'pricing')} className="text-sm text-gray-600 hover:text-[#14532d] transition font-medium">
                        Pricing {faqOpen === 'pricing' ? '↑' : '↓'}
                      </button>
                    </div>
                  </div>
                  {faqOpen && (
                    <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 px-5 py-4 text-sm text-gray-600 leading-relaxed max-w-2xl">
                      {faqOpen === 'delivery' && 'Delivery coordination varies by supplier and location. Your delivery day is confirmed at checkout after supplier acceptance.'}
                      {faqOpen === 'payment' && 'Payment terms are set by the supplier and shown at checkout. Add your email at checkout for a receipt or invoice.'}
                      {faqOpen === 'traceability' && 'Every listing shows the harvest date, origin, and supplier. Product details include handling steps from harvest to delivery where available.'}
                      {faqOpen === 'pricing' && 'Prices are per commercial unit as listed. Checkout shows a breakdown of quantities, unit prices, and totals.'}
                    </div>
                  )}
                </div>

                {/* For producers */}
                <div className="mt-10 flex flex-col items-start gap-4 rounded-2xl bg-[#06150e] px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.24em] text-[#f3d978]">For producers</p>
                    <p className="mt-2 text-lg font-black text-white tracking-tight">Sell directly — no middlemen.</p>
                  </div>
                  <Link to={dashboardLoginUrl} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#d4af37] px-6 py-3 text-sm font-black text-[#06150e] transition hover:-translate-y-0.5 hover:bg-[#f0d36c]">
                    Open your workspace →
                  </Link>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {checkoutError && (
        <div role="alert" className="fixed bottom-5 left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-lg border border-red-200 bg-white px-4 py-3 shadow-lg">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm font-medium text-red-700">{checkoutError}</p>
            <button type="button" onClick={() => setCheckoutError(null)} className="text-sm font-semibold text-red-700 hover:text-red-900">Dismiss</button>
          </div>
        </div>
      )}

      {viewMode === 'checkout' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <CheckoutForm
            items={cartItems}
            totalValue={totalCartValue}
            onSubmit={handleSubmitOrder}
            onBack={() => { setViewMode('catalog'); setIsCartOpen(true); }}
          />
        </div>
      )}

      {viewMode === 'confirmation' && confirmedOrder && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <CheckoutResult
            orderNumber={confirmedOrder.orderNumber}
            items={confirmedOrder.items}
            totalValue={confirmedOrder.totalValue}
            currency={confirmedOrder.currency}
            deliveryDay={confirmedOrder.deliveryDay}
            deliveryDate={confirmedOrder.deliveryDate}
            buyerName={confirmedOrder.buyerName}
            onContinueShopping={() => { navigate('/orders'); }}
          />
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedListing && d && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" onClick={() => setSelectedListing(null)} />
            <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Close button */}
              <button
                onClick={() => setSelectedListing(null)}
                className="absolute top-4 right-4 z-10 p-2 bg-white/80 backdrop-blur rounded-full hover:bg-white transition-colors"
                aria-label="Close product detail"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>

              {/* ─── Image ──────────────────────────────────────────────── */}
              <div className="aspect-[16/9] bg-gray-100 relative rounded-t-2xl overflow-hidden">
                {d.image_url ? (
                  <img src={d.image_url} alt={d.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#f7f9f4]">
                    <span className="text-7xl">🌿</span>
                  </div>
                )}
                {/* Overlays */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {d.traceability_enabled && (
                    <span className="px-3 py-1.5 bg-[#14532d] text-white text-xs font-bold rounded-lg flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Traceable
                    </span>
                  )}
                  {d.supplier_verified === 'verified' && (
                    <span className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                      Verified Supplier
                    </span>
                  )}
                </div>
                {d.harvest_start_date && (
                  <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur text-white text-xs font-bold rounded-lg">
                    Harvest: {d.harvest_start_date}{d.harvest_end_date ? ` — ${d.harvest_end_date}` : ''}
                  </div>
                )}
              </div>

              <div className="p-6">
                {/* ─── Title + Price ────────────────────────────────────── */}
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">{d.title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {d.catalog_name}{d.catalog_category ? ` · ${d.catalog_category}` : ''}{d.crop_type ? ` · ${d.crop_type}` : ''}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {d.grade && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">Grade: {d.grade}</span>}
                      {d.certification && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded">{d.certification}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-extrabold text-emerald-600">{d.currency || DEFAULT_CURRENCY} {Number(d.price_per_unit || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-400">per {d.commercial_unit}</div>
                    <div className="mt-1 flex items-center gap-1 justify-end">
                      {(d.available_quantity || 0) > 0 ? (
                        <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> In stock
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 font-medium">Out of stock</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* ─── Supplier Profile Card ────────────────────────────── */}
                <div className="border border-gray-200 rounded-xl p-4 mb-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      {d.supplier_logo ? (
                        <img src={d.supplier_logo} alt="" className="w-full h-full object-cover rounded-xl" />
                      ) : (
                        <span className="text-sm font-bold text-emerald-700">{d.supplier_name?.[0] || 'S'}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm text-gray-900">{d.supplier_name}</span>
                        {d.supplier_verified === 'verified' && (
                          <svg className="w-4 h-4 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{d.supplier_type || 'Agricultural Supplier'} · {d.supplier_city}{d.supplier_country ? `, ${d.supplier_country}` : ''}</p>
                      {d.supplier_description && (
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{d.supplier_description}</p>
                      )}
                      <div className="flex flex-wrap gap-3 mt-2">
                        {d.supplier_email && (
                          <a href={`mailto:${d.supplier_email}`} className="text-xs text-blue-600 hover:underline">{d.supplier_email}</a>
                        )}
                        {d.supplier_phone && (
                          <a href={`tel:${d.supplier_phone}`} className="text-xs text-blue-600 hover:underline">{d.supplier_phone}</a>
                        )}
                      </div>
                      {d.supplier_address && (
                        <p className="text-xs text-gray-400 mt-1">{d.supplier_address}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* ─── Description ──────────────────────────────────────── */}
                {(d.description || d.catalog_description) && (
                  <div className="mb-5">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">About this product</h3>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{d.description || d.catalog_description}</p>
                  </div>
                )}

                {/* ─── Spec Grid ───────────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                  {[
                    { label: 'Commercial Unit', value: d.commercial_unit },
                    { label: 'Grade', value: d.grade || 'Standard' },
                    { label: 'Min Order', value: `${d.minimum_order_quantity || 1} ${d.commercial_unit}` },
                    { label: 'Available', value: `${d.available_quantity || 0} ${d.commercial_unit}` },
                    { label: 'Max Order', value: d.maximum_order_quantity ? `${d.maximum_order_quantity} ${d.commercial_unit}` : 'No limit' },
                    { label: 'Conversion', value: d.conversion_factor && d.conversion_factor !== 1 ? `${d.conversion_factor} ${d.normalized_unit || d.commercial_unit} / ${d.commercial_unit}` : null },
                  ].filter(s => s.value).map(s => (
                    <div key={s.label} className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-[10px] font-bold text-gray-400 uppercase">{s.label}</span>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* ─── Tab Navigation ───────────────────────────────────── */}
                <div className="border-b border-gray-200 mb-5">
                  <div className="flex gap-0 -mb-px">
                    {([
                      { key: 'overview' as const, label: 'Overview' },
                      { key: 'traceability' as const, label: 'Traceability' },
                      { key: 'delivery' as const, label: 'Delivery' },
                    ]).map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setDetailTab(tab.key)}
                        className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
                          detailTab === tab.key
                            ? 'border-emerald-500 text-emerald-700'
                            : 'border-transparent text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ─── Tab: Overview ────────────────────────────────────── */}
                {detailTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {d.catalog_category && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-[10px] font-bold text-gray-400 uppercase">Category</span>
                          <p className="text-sm font-bold text-gray-900 mt-0.5 capitalize">{d.catalog_category}</p>
                        </div>
                      )}
                      {d.crop_type && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-[10px] font-bold text-gray-400 uppercase">Crop Type</span>
                          <p className="text-sm font-bold text-gray-900 mt-0.5 capitalize">{d.crop_type}</p>
                        </div>
                      )}
                      {d.tenant_name && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-[10px] font-bold text-gray-400 uppercase">Producer</span>
                          <p className="text-sm font-bold text-gray-900 mt-0.5">{d.tenant_name}</p>
                        </div>
                      )}
                      {d.currency && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-[10px] font-bold text-gray-400 uppercase">Currency</span>
                          <p className="text-sm font-bold text-gray-900 mt-0.5">{d.currency}</p>
                        </div>
                      )}
                    </div>
                    {/* Related listings */}
                    {d.related && d.related.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">From the same supplier</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {d.related.slice(0, 3).map((r: Listing) => (
                            <div key={r.id} className="p-2 bg-gray-50 rounded-lg">
                              <p className="text-xs font-bold text-gray-900 truncate">{r.title}</p>
                              <p className="text-xs text-emerald-600 font-bold mt-0.5">{r.currency || DEFAULT_CURRENCY} {Number(r.price_per_unit || 0).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ─── Tab: Traceability ────────────────────────────────── */}
                {detailTab === 'traceability' && (
                  <div className="space-y-4">
                    {!d.traceability_enabled ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <p className="text-sm text-gray-500">Traceability is not enabled for this listing.</p>
                      </div>
                    ) : (
                      <>
                        {/* Harvest info */}
                        <div className="p-4 bg-[#f7f9f4] border border-[#dbe5d2] rounded-xl">
                          <h4 className="text-xs font-bold text-[#14532d] uppercase tracking-wider mb-3">Harvest Information</h4>
                          <div className="grid grid-cols-2 gap-3">
                            {d.harvest_start_date && (
                              <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase">Start Date</span>
                                <p className="text-sm font-bold text-gray-900">{d.harvest_start_date}</p>
                              </div>
                            )}
                            {d.harvest_end_date && (
                              <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase">End Date</span>
                                <p className="text-sm font-bold text-gray-900">{d.harvest_end_date}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Traceability timeline */}
                        <div className="relative pl-6">
                          <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-emerald-200" />
                          {[
                            d.supplier_name && { step: 'Source', detail: `Supplied by ${d.supplier_name}`, location: d.supplier_city, icon: '🏪' },
                            d.fulfillment_location && { step: 'Location', detail: d.fulfillment_location, location: d.fulfillment_city, icon: '📍' },
                            d.harvest_start_date && { step: 'Harvest', detail: `Harvested ${d.harvest_start_date}${d.harvest_end_date ? ` — ${d.harvest_end_date}` : ''}`, icon: '🌾' },
                            d.grade && { step: 'Graded', detail: `Grade: ${d.grade}`, icon: '✓' },
                            { step: 'Listed', detail: d.published_at ? `Listed ${new Date(d.published_at).toLocaleDateString()}` : 'Listed on marketplace', icon: '📋' },
                          ].filter(Boolean).map((item: any, i: number) => (
                            <div key={i} className="relative mb-4 last:mb-0">
                              <div className="absolute -left-[19px] top-0 w-8 h-8 bg-white border-2 border-emerald-300 rounded-full flex items-center justify-center text-sm">
                                {item.icon}
                              </div>
                              <div className="ml-4">
                                <p className="text-sm font-bold text-gray-900">{item.step}</p>
                                <p className="text-xs text-gray-600">{item.detail}</p>
                                {item.location && <p className="text-xs text-gray-400">{item.location}</p>}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Source lots */}
                        {d.source_harvest_lots && (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <span className="text-[10px] font-bold text-gray-400 uppercase">Source Harvest Lots</span>
                            <p className="text-sm text-gray-700 mt-1 font-mono">{typeof d.source_harvest_lots === 'string' ? d.source_harvest_lots : JSON.stringify(d.source_harvest_lots)}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ─── Tab: Delivery ────────────────────────────────────── */}
                {detailTab === 'delivery' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {d.fulfillment_location && (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            <span className="text-xs font-bold text-gray-500 uppercase">Fulfillment Location</span>
                          </div>
                          <p className="text-sm font-bold text-gray-900">{d.fulfillment_location}</p>
                          {d.fulfillment_city && <p className="text-xs text-gray-500 mt-0.5">{d.fulfillment_city}</p>}
                        </div>
                      )}
                      {d.delivery_areas && (
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                            <span className="text-xs font-bold text-gray-500 uppercase">Delivery Areas</span>
                          </div>
                          <p className="text-sm text-gray-700">{typeof d.delivery_areas === 'string' ? d.delivery_areas : JSON.stringify(d.delivery_areas)}</p>
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-[#f7f9f4] border border-[#dbe5d2] rounded-xl">
                      <h4 className="text-xs font-bold text-[#14532d] uppercase tracking-wider mb-2">How delivery works</h4>
                      <ul className="space-y-1.5 text-sm text-gray-600">
                        <li className="flex items-start gap-2">
                          <span className="text-[#14532d] mt-0.5">1.</span>
                          <span>Place your order at checkout with a delivery date and address.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#14532d] mt-0.5">2.</span>
                          <span>The supplier confirms availability and delivery terms.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#14532d] mt-0.5">3.</span>
                          <span>Track your shipment status in the Buyer Portal.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* ─── Add to Cart + Actions ──────────────────────────── */}
                <div className="flex gap-3 mt-6 pt-5 border-t border-gray-100">
                  {(() => {
                    const constraints = constraintsFromListing(d);
                    const orderable = isOrderable(constraints);
                    return (
                      <button
                        onClick={() => { handleAddToCart(d, d.minimum_order_quantity || 1); setSelectedListing(null); }}
                        disabled={!orderable}
                        className="flex-1 py-3.5 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {!orderable
                          ? 'Minimum order unavailable'
                          : `Add to Cart · ${d.minimum_order_quantity || 1} ${d.commercial_unit} min`}
                      </button>
                    );
                  })()}
                  {isAuthenticated && (
                    <Link
                      to="/account/rfqs/new"
                      state={{ prefill: { title: `${d.title} — Bulk Request`, items: [{ title: d.title, commercial_unit: d.commercial_unit || 'kg', commercial_quantity: d.minimum_order_quantity || 1, grade: d.grade || '' }] } }}
                      onClick={() => setSelectedListing(null)}
                      className="px-5 py-3.5 rounded-xl font-bold border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50 transition-all text-sm text-center"
                    >
                      Request Quote
                    </Link>
                  )}
                  <button
                    onClick={async () => {
                      const shareData = {
                        title: d.title,
                        text: `${d.title} — ${Number(d.price_per_unit || 0).toLocaleString()} ${(d.currency || marketCurrency)} per ${d.commercial_unit} on eSanza Marketplace`,
                        url: window.location.href,
                      };
                      try {
                        if (navigator.share) { await navigator.share(shareData); }
                        else if (navigator.clipboard) { await navigator.clipboard.writeText(shareData.url); }
                      } catch { /* dismissed */ }
                    }}
                    className="px-5 py-3.5 rounded-xl font-bold border-2 border-gray-200 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                    aria-label="Share listing"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  </button>
                </div>

                {/* Loading overlay for detail fetch */}
                {detailLoading && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-20">
                    <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shopping Cart Sidebar */}
      <ShoppingCart
        items={cartItems}
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onCheckout={handleCheckout}
        onRequestQuote={() => {
          setIsCartOpen(false);
          // Navigate with cart items as pre-fill state for RFQ
          const rfqItems = cartItems.map(item => ({
            title: item.cropType || item.catalogName || item.title || '',
            commercial_unit: item.unit || 'kg',
            commercial_quantity: item.quantity || 1,
          }));
          navigate('/account/rfqs/new', { state: { prefill: { items: rfqItems } } });
        }}
      />
    </div>
  );
};

export default Marketplace;
